/**
 * IntegrationSetup — interrupt-driven card for connecting external integrations.
 *
 * When the agent calls `initiate_integration`, the graph interrupts with
 * { type: 'integration_setup', ... }. This component replaces the composer,
 * renders a redirect button + an "I'm done" button. Clicking "I'm done"
 * resumes the graph so the agent can continue the onboarding flow.
 */

import { useCallback, useState, type FC } from 'react';
import { useLangGraphInterruptState, useLangGraphSendCommand } from '@assistant-ui/react-langgraph';
import { ArrowUpRightIcon, CheckIcon, LoaderIcon } from 'lucide-react';
import { Button } from '@/client/components/ui/button';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IntegrationType = 'shopify' | 'email';
type RedirectTarget = 'external' | 'parent';

interface IntegrationSetupValue {
  type: 'integration_setup';
  integration_type: IntegrationType;
  title: string;
  description: string;
  redirect_url: string;
  redirect_target: RedirectTarget;
}

function isIntegrationSetupValue(v: unknown): v is IntegrationSetupValue {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    o.type === 'integration_setup' &&
    typeof o.integration_type === 'string' &&
    typeof o.title === 'string' &&
    typeof o.redirect_url === 'string' &&
    (o.redirect_target === 'external' || o.redirect_target === 'parent')
  );
}

// ---------------------------------------------------------------------------
// Hook — true when the graph is paused on an integration_setup interrupt
// ---------------------------------------------------------------------------

export function useIntegrationSetupValue(): IntegrationSetupValue | null {
  const interruptState = useLangGraphInterruptState();
  const value = interruptState?.value;
  return isIntegrationSetupValue(value) ? value : null;
}

export function useIsIntegrationSetupMode(): boolean {
  return useIntegrationSetupValue() !== null;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export const IntegrationSetup: FC = () => {
  const value = useIntegrationSetupValue();
  const sendCommand = useLangGraphSendCommand();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleViewPage = useCallback(() => {
    if (!value) return;
    if (value.redirect_target === 'external') {
      window.open(value.redirect_url, '_blank', 'noopener,noreferrer');
      return;
    }
    window.parent.postMessage(
      { type: 'GAIA_EMBED_NAVIGATE', url: value.redirect_url },
      '*',
    );
  }, [value]);

  const handleDone = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await sendCommand({ resume: true as unknown as string });
    } finally {
      setIsSubmitting(false);
    }
  }, [sendCommand]);

  if (!value) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-input bg-background">
      <div className="flex flex-col gap-2 px-4 py-3">
        <span className="text-[13px] font-semibold text-foreground">{value.title}</span>
        <p className="text-[12px] leading-snug text-muted-foreground">{value.description}</p>
      </div>
      <div className="flex items-center justify-end gap-2 border-t px-3 py-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 text-xs"
          onClick={handleViewPage}
          disabled={isSubmitting}
        >
          View page
          <ArrowUpRightIcon className="size-3" />
        </Button>
        <Button
          size="sm"
          className="h-8 gap-1 bg-[hsl(262_60%_55%)] text-xs text-white hover:bg-[hsl(262_60%_45%)]"
          onClick={() => void handleDone()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <LoaderIcon className="size-3 animate-spin" />
          ) : (
            <CheckIcon className="size-3" />
          )}
          I'm done
        </Button>
      </div>
    </div>
  );
};
